import { useMemo } from 'react';
import { css } from '@emotion/react';
import { AnimatePresence, motion, Variants } from 'motion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { PanelContent, PanelHeader, PanelPortal } from '@frontend/panel-engine';
import { theme } from '@frontend/theme';
import { Button } from '@frontend/design-system';
import { AppointmentDetailsPanel } from '../../components/appointment-panel/appointment-details-panel';
import { OverlappingAppointmentsPanel } from '../../components/appointment-panel/overlapping-appointments-panel';
import { useOpenAppointmentEditPanel } from '../../components/schedule-calendar-components/appointment-event-card/edit-appointment-action-button';
import { useScheduleAppointmentInfoPanelShallowStore } from '../../stores/schedule-appointment-info-panel.store';

type PanelContentType = 'list-view' | 'details-view';

const fadeVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

export const ScheduleAppointmentInfoPanel = () => {
  const { t } = useTranslation('schedule');
  const { selectedAppointmentId, selectedAppointment, selectedAppointments, resetSelection } =
    useScheduleAppointmentInfoPanelShallowStore(
      'selectedAppointmentId',
      'selectedAppointment',
      'selectedAppointments',
      'resetSelection'
    );
  const { openAppointmentEditPanel } = useOpenAppointmentEditPanel(selectedAppointment);
  const { hasWSM } = SchedulerV3.Helpers.useCheckHasWSM(selectedAppointment?.locationId);

  const activeContentType = useMemo<PanelContentType>(() => {
    if (selectedAppointmentId) {
      return 'details-view';
    } else if (selectedAppointments.length > 0) {
      return 'list-view';
    }

    return 'list-view';
  }, [selectedAppointmentId, selectedAppointments.length]);

  const PanelComponent = useMemo(() => {
    if (activeContentType === 'details-view') {
      return AppointmentDetailsPanel;
    } else if (activeContentType === 'list-view') {
      return OverlappingAppointmentsPanel;
    } else {
      return OverlappingAppointmentsPanel;
    }
  }, [activeContentType]);

  if (!selectedAppointments.length && !selectedAppointmentId) return null;

  const isDetailsView = activeContentType === 'details-view';
  const shouldShowEditButton = isDetailsView && hasWSM;

  return (
    <PanelPortal enableAnimations id='schedule-appointment-info' width='smallXL' onClose={resetSelection}>
      <PanelHeader css={headerStyle}>
        <PanelHeader.Title level={3}>{t('Appointment Details')}</PanelHeader.Title>
        <div css={headerActionsStyle}>
          {shouldShowEditButton && (
            <Button variant='tertiary' iconName='edit-small' onClick={() => openAppointmentEditPanel()}>
              {t('Edit')}
            </Button>
          )}
          <PanelHeader.Close iconName='x' size='large' />
        </div>
      </PanelHeader>

      <PanelContent padding={false} css={panelContentStyle}>
        <AnimatePresence mode='wait'>
          <motion.div key={activeContentType} variants={fadeVariants} initial='initial' animate='animate' exit='exit'>
            <PanelComponent />
          </motion.div>
        </AnimatePresence>
      </PanelContent>
    </PanelPortal>
  );
};

const headerStyle = css({
  padding: theme.spacing(1, 2),
});

const headerActionsStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
});

const panelContentStyle = css({
  display: 'flex',
  borderTop: `1px solid ${theme.colors.neutral20}`,
  position: 'relative',
  overflow: 'hidden',
  '> div': { width: '100%', flex: 1 },
});
